import { useEffect } from "react";
import { Navigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight } from "lucide-react";
import SiteLayout from "@/components/site/SiteLayout";
import { GUIDES, getGuideBySlug, findGuideByAnySlug } from "@/content/guides";
import { guidesCopy } from "@/content/pages";
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, getTranslations, type Locale } from "@/i18n/translations";
import NotFound from "./NotFound";

const BASE_URL = "https://nellove.com";

const GuideArticle = () => {
  const { locale, slug } = useParams<{ locale: string; slug: string }>();
  const currentLocale = (SUPPORTED_LOCALES.includes(locale as Locale) ? locale : DEFAULT_LOCALE) as Locale;
  const t = getTranslations(currentLocale);
  const copy = guidesCopy[currentLocale];

  const guide = slug ? getGuideBySlug(currentLocale, slug) : undefined;
  const crossLocale = !guide && slug ? findGuideByAnySlug(slug) : undefined;

  useEffect(() => {
    document.documentElement.lang = currentLocale;
  }, [currentLocale]);

  if (!guide) {
    if (crossLocale) {
      return <Navigate to={`/${currentLocale}/guides/${crossLocale.content[currentLocale].slug}`} replace />;
    }
    return <NotFound />;
  }

  const body = guide.content[currentLocale];
  const canonical = `${BASE_URL}/${currentLocale}/guides/${body.slug}`;
  const others = GUIDES.filter((g) => g.id !== guide.id);
  const otherLocale: Locale = currentLocale === "en" ? "fr" : "en";

  return (
    <SiteLayout
      locale={currentLocale}
      t={t}
      altPath={`/${otherLocale}/guides/${guide.content[otherLocale].slug}`}
    >
      <Helmet>
        <html lang={currentLocale} />
        <title>{body.metaTitle}</title>
        <meta name="description" content={body.description} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="en" href={`${BASE_URL}/en/guides/${guide.content.en.slug}`} />
        <link rel="alternate" hrefLang="fr" href={`${BASE_URL}/fr/guides/${guide.content.fr.slug}`} />
        <link rel="alternate" hrefLang="x-default" href={`${BASE_URL}/en/guides/${guide.content.en.slug}`} />
        <meta property="og:title" content={body.metaTitle} />
        <meta property="og:description" content={body.description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={`${BASE_URL}/images/nellove-cover.png`} />
        <meta property="og:site_name" content="Nellove" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={body.metaTitle} />
        <meta name="twitter:description" content={body.description} />
        <meta name="twitter:image" content={`${BASE_URL}/images/nellove-cover.png`} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Article",
                headline: body.title,
                description: body.description,
                inLanguage: currentLocale === "fr" ? "fr-CA" : "en-CA",
                datePublished: guide.updated,
                dateModified: guide.updated,
                mainEntityOfPage: canonical,
                author: { "@type": "Organization", name: "Nellove", url: BASE_URL },
                publisher: {
                  "@type": "Organization",
                  name: "Nellove",
                  url: BASE_URL,
                  logo: `${BASE_URL}/nellove-logo.png`,
                },
              },
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: copy.breadcrumbHome, item: `${BASE_URL}/${currentLocale}` },
                  { "@type": "ListItem", position: 2, name: copy.eyebrow, item: `${BASE_URL}/${currentLocale}/guides` },
                  { "@type": "ListItem", position: 3, name: body.title, item: canonical },
                ],
              },
            ],
          })}
        </script>
      </Helmet>

      <article className="px-5 py-12 sm:px-6 md:py-16">
        <div className="mx-auto max-w-2xl">
          <nav aria-label="Breadcrumb" className="text-[13px] text-stone-600">
            <a href={`/${currentLocale}/guides`} className="text-rust-600 underline-offset-4 hover:underline">
              {copy.backToGuides}
            </a>
          </nav>
          <h1 className="mt-4 text-[34px] leading-[1.1] tracking-tight sm:text-[42px]">{body.title}</h1>
          <p className="mt-3 text-[13px] text-stone-600">
            {copy.updatedLabel} {guide.updated} · {body.readingTime}
          </p>
          <p className="mt-6 text-[18px] leading-relaxed text-stone-700">{body.intro}</p>

          {body.sections.map((section) => (
            <section key={section.heading} className="mt-10">
              <h2 className="font-serif text-[26px] leading-snug tracking-tight">{section.heading}</h2>
              {section.paragraphs?.map((p) => (
                <p key={p} className="mt-4 text-[17px] leading-relaxed text-stone-700">
                  {p}
                </p>
              ))}
              {section.list && (
                <ul className="mt-4 space-y-2 border-l border-stone-200 pl-5">
                  {section.list.map((item) => (
                    <li key={item} className="text-[16px] leading-relaxed text-stone-700">
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}

          <p className="mt-10 border-t border-stone-200 pt-6 text-[17px] leading-relaxed text-stone-700">
            {body.closing}
          </p>

          <div className="mt-10 rounded-xl border border-stone-200 bg-secondary p-6">
            <h2 className="font-serif text-[24px] tracking-tight">{copy.ctaHeading}</h2>
            <p className="mt-3 text-[16px] leading-relaxed text-stone-600">{copy.ctaBody}</p>
            <a
              href={`/${currentLocale}#join`}
              className="mt-5 inline-flex h-12 items-center gap-2 rounded-xl bg-primary px-6 text-base font-semibold text-primary-foreground transition-colors duration-200 hover:bg-rust-600"
            >
              {copy.ctaButton}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </a>
          </div>

          <div className="mt-10">
            <h2 className="font-serif text-[20px] tracking-tight">{copy.backToGuides}</h2>
            <ul className="mt-3 space-y-2">
              {others.map((g) => (
                <li key={g.id}>
                  <a
                    href={`/${currentLocale}/guides/${g.content[currentLocale].slug}`}
                    className="text-[16px] text-rust-600 underline-offset-4 hover:underline"
                  >
                    {g.content[currentLocale].title}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </article>
    </SiteLayout>
  );
};

export default GuideArticle;
